import type { BoltFxRateSnapshot } from "./boltutil-fx-types";
import { ApiError } from "./api";

const SCALE = 100_000_000n;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

function decimalUnits(value: unknown): bigint | null {
  if (typeof value !== "string" || value.trim() !== value || !/^(?:0|[1-9]\d?)(?:\.\d{1,8})?$/.test(value)) return null;
  const [whole, fraction = ""] = value.split(".");
  return BigInt(whole) * SCALE + BigInt(fraction.padEnd(8, "0"));
}

/** Match the server's legacy approval policy; do not silently normalize input. */
export function isBoltLegacyRate(value: unknown): value is string {
  if (typeof value !== "string" || value.trim() !== value || !/^(?:[4-9]|1[0-2])(?:\.\d{1,8})?$/.test(value)) return false;
  return decimalUnits(value)! <= 12n * SCALE;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isoTimestamp(value: unknown): number | null {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value ? timestamp : null;
}

/** Validate reference data for explicit form filling, never for money settlement. */
export function readBoltLegacyReference(response: unknown, elapsedMs = 0): BoltFxRateSnapshot | null {
  if (!isRecord(response) || !isRecord(response.rate) || !Number.isFinite(elapsedMs) || elapsedMs < 0 || elapsedMs > 15000) return null;
  // Server time is authoritative; elapsed monotonic time conservatively includes transport delay.
  const serverTime = isoTimestamp(response.serverTime);
  if (serverTime === null) return null;
  const rate = response.rate;
  if (!isBoltLegacyRate(rate.cnyPerUsdt) || !isRecord(rate.sources)
    || rate.sources.crypto !== "kraken" || rate.sources.fiat !== "frankfurter-ecb") return null;

  const usdtUsd = decimalUnits(rate.usdtUsd);
  const usdCny = decimalUnits(rate.usdCny);
  const combined = decimalUnits(rate.cnyPerUsdt)!;
  if (usdtUsd === null || usdtUsd < 90_000_000n || usdtUsd > 110_000_000n
    || usdCny === null || usdCny < 4n * SCALE || usdCny > 12n * SCALE
    || rate.cnyPerUsdtUnits !== combined.toString()
    || (usdtUsd * usdCny + SCALE / 2n) / SCALE !== combined) return null;

  const observed = isoTimestamp(rate.observedAt);
  if (observed === null || observed > serverTime || serverTime - observed + elapsedMs >= HOUR_MS) return null;

  if (typeof rate.fiatDate !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(rate.fiatDate)) return null;
  const fiatDay = Date.parse(`${rate.fiatDate}T00:00:00.000Z`);
  const today = Math.floor(serverTime / DAY_MS) * DAY_MS;
  if (!Number.isFinite(fiatDay) || new Date(fiatDay).toISOString().slice(0, 10) !== rate.fiatDate
    || fiatDay > today || today - fiatDay > 7 * DAY_MS) return null;

  return {
    usdtUsd: rate.usdtUsd as string, usdCny: rate.usdCny as string,
    cnyPerUsdt: rate.cnyPerUsdt, cnyPerUsdtUnits: combined.toString(),
    observedAt: rate.observedAt as string, fiatDate: rate.fiatDate,
    sources: { crypto: "kraken", fiat: "frankfurter-ecb" },
  };
}

export class BoltLegacyReferenceDataError extends Error {
  constructor() { super("Invalid reference data"); this.name = "BoltLegacyReferenceDataError"; }
}

/** Actionable local copy only; never render raw service errors or transport details. */
export function boltLegacyReferenceError(reason: unknown): string {
  if (reason instanceof ApiError) {
    if (reason.status === 401) return "登录状态已失效，原输入已保留。请重新登录后重试。";
    if (reason.status === 403) return "暂无参考汇率查询权限，原输入已保留。请重新登录或联系管理员确认权限。";
    if (reason.status === 429) return "参考汇率查询过于频繁，原输入已保留。请稍后重试，或填写已批准汇率。";
    if (reason.status === 503) return "参考汇率服务暂不可用，原输入已保留。请稍后重试，或填写已批准汇率。";
    return "参考汇率请求未成功，原输入已保留。请稍后重试，或联系管理员。";
  }
  if (reason instanceof BoltLegacyReferenceDataError) return "参考汇率数据格式或时效未通过校验，原输入已保留。请稍后重试，或填写已批准汇率。";
  if (isRecord(reason) && reason.name === "TimeoutError") return "参考汇率请求超时，原输入已保留。请检查网络后重试，或填写已批准汇率。";
  if (reason instanceof TypeError) return "网络连接失败，原输入已保留。请检查网络后重试，或填写已批准汇率。";
  return "参考汇率获取未完成，原输入已保留。请重试，或联系管理员。";
}
